import request, { AxiosResponse } from "axios";
import { apiBase } from "../../constants";

const rethrow = (e: any): never => {
  throw new Error(
    e?.response?.data?.error?.message ||
      e.message ||
      "Something went wrong. Please try again."
  );
};

export interface MfaStatus {
  enabled: boolean;
  pending: boolean;
  recoveryCodesLeft: number;
}

export const getMfaStatus = async (): Promise<MfaStatus> => {
  try {
    const res: AxiosResponse<MfaStatus> = await request({
      url: `${apiBase}/v1/team-members/me/mfa`,
      method: "GET",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrow(e);
  }
};

export const startMfaEnrollment = async (
  opts: {
    /** mint a fresh key even though another account already holds one */
    regenerate?: boolean;
  } = {}
): Promise<{
  /**
   * Absent when the person already proved a factor in another account: the
   * server reuses that secret so the authenticator keeps ONE entry, and it
   * never sends the secret back - they prove possession with a code from
   * the entry they already have.
   */
  secret?: string;
  otpauthUrl?: string;
  reusedExistingAuthenticator?: boolean;
}> => {
  try {
    const res = await request({
      url: `${apiBase}/v1/team-members/me/mfa/enroll`,
      method: "POST",
      data: opts,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrow(e);
  }
};

export const activateMfa = async (
  code: string
): Promise<{ recoveryCodes: string[] }> => {
  try {
    const res = await request({
      url: `${apiBase}/v1/team-members/me/mfa/activate`,
      method: "POST",
      data: { code },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    return rethrow(e);
  }
};

export const disableMfa = async (code: string): Promise<void> => {
  try {
    await request({
      url: `${apiBase}/v1/team-members/me/mfa/disable`,
      method: "POST",
      data: { code },
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    rethrow(e);
  }
};

/** Admin: reset a team member's MFA (requires team administration rights). */
export const resetTeamMemberMfa = async (
  teamMemberId: number
): Promise<void> => {
  try {
    await request({
      url: `${apiBase}/v1/team-members/${teamMemberId}/mfa/reset`,
      method: "POST",
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
  } catch (e) {
    rethrow(e);
  }
};

/**
 * Broadcast that this member's own 2FA status changed.
 *
 * The enforcement banner/takeover reads the status once when it mounts, and it
 * lives in a different part of the tree from the profile settings - so without
 * a signal, enrolling from Your Profile leaves the "you have N days" banner on
 * screen until the next full page load, which reads as the enrolment not having
 * worked.
 */
export const MFA_STATUS_CHANGED_EVENT = "encharge:mfa-status-changed";

export const emitMfaStatusChanged = (enabled: boolean) => {
  try {
    window.dispatchEvent(
      new CustomEvent(MFA_STATUS_CHANGED_EVENT, { detail: { enabled } })
    );
  } catch (e) {
    // a browser without CustomEvent is not worth failing enrolment over
  }
};
